/**
 * ShapeDesignerPanel — inspector for the decorative-wall layer in the Synth VR editor.
 *
 * Toggled by the "Shapes" button under the Obstacles grid
 * (store.shapeDesignerOpen). Lists the decor groups on the current map and, for
 * the selected group, lets you tune how one decorative wall repeats into a line:
 *   • shape   — which obstacle mesh to use
 *   • axis    — repeat along X / Y (space) or Z (time, in beats)
 *   • count   — how many walls
 *   • spacing — gap between them
 *   • curve   — bend the line with a preset (arc / s-curve / wave / zigzag)
 *   • tip     — straight ends, or taper the extreme walls smaller
 *   • color / scale / rotation / origin — look & placement
 *
 * Groups persist per-map in a localStorage draft (see editorStore decor slice);
 * SynthVR has no backend save for this decorative layer. Non-modal floating card
 * matching FlashEventPopup; close with ×, Escape, or the toggle button.
 */

import React, { useEffect, useState } from 'react';
import { useEditorStore } from '../../store/editorStore';
import type { DesignShape, DesignMesh, DesignAxis, DesignCurve, DesignTip, DesignMirror, DesignGradientDir, DesignAnimPreset, DesignAnimSync, DesignAnimDist, DesignAnimConfig } from '../../types/designShape';
import { DESIGN_MESHES, countDesignMeshes, designBeatRange, makeDesignMesh, distinctNeonColor, makeDefaultCustomPath, makeDesignAnim, isShapeCurve, designAnimUsesSpread, DESIGN_ANIM_PRESETS, DESIGN_ANIM_DISTS, DEFAULT_CUSTOM_PATH, PRIMARY_MESH_ID, resolveAxes, QUEST_SOLID_WALL_CAP } from '../../types/designShape';
import type { ObstacleShape } from '../../store/editorSettings';
import { color, font } from '../ui/theme';
import { IcTrash, IcPlus, IcChevLeft, IcClose, IcLock, IcUnlock, IcCopy, IcEye, IcEyeOff, IcStar, IcPin } from '../ui/icons';
import GradientSlider from '../ui/GradientSlider';
import StepperInput from '../ui/StepperInput';
import { useDraggablePanel } from '../ui/useDraggablePanel';

const round = (x: number, d: number) => { const f = Math.pow(10, d); return Math.round(x * f) / f; };
const LABEL_W = 62;

// Slider ranges (GradientSlider works in 0..1).
const SPACE_MIN = 0.01, SPACE_MAX = 4;     // spacing — floor is tiny so walls can pack tight / overlap
const AMP_MIN = 0, AMP_MAX = 6;            // curve amplitude
const TAP_MIN = 0, TAP_MAX = 1;            // taper strength
const TAPLEN_MIN = 0.05, TAPLEN_MAX = 1;   // taper length (fraction of each half)
const SCALE_MIN = 0.05, SCALE_MAX = 10;    // wall scale (uniform)
const SCALEZ_MIN = 0.05, SCALEZ_MAX = 300; // depth-only Z scale — huge range for long "beam" walls
const ROT_MIN = -180, ROT_MAX = 180;       // rotation (deg)
const TWIST_MIN = -1800, TWIST_MAX = 1800; // graded twist across the line (deg) — up to ±5 full turns
const REP_MIN = 0.25, REP_MAX = 24;        // module-repeat gap
const ORG_MIN = -4, ORG_MAX = 4;           // part offset X / Y (local, relative to the primary)
const OFFSET_Z_MIN = -4, OFFSET_Z_MAX = 4; // part time offset (beats)
const MOVE_XY_MIN = -12, MOVE_XY_MAX = 12; // Global Position Move X/Y — 3× the old ±4 range
const MOVE_Z_MIN = 0, MOVE_Z_MAX = 400;    // Global Position Move Z (beats) — generous span for a full song
const SPREAD_XY_MIN = -8, SPREAD_XY_MAX = 8;   // Expand X/Y — pre-symmetry local offset
const SPREAD_Z_MIN = -8, SPREAD_Z_MAX = 8;     // Expand Z (beats) — same, time axis
const CUSTOM_XY_MIN = -8, CUSTOM_XY_MAX = 8;   // custom-path node X / Y (local, app units)
const CUSTOM_Z_MIN = -12, CUSTOM_Z_MAX = 12;   // custom-path node Z (beat offset)
const ANIM_SPEED_MIN = 0.1, ANIM_SPEED_MAX = 8;  // animation rate
const ANIM_AMT_MIN = 0, ANIM_AMT_MAX = 2;        // animation strength / amplitude
const ANIM_PHASE_MIN = 0, ANIM_PHASE_MAX = 3;    // animation spatial spread (wavelength)
const ANIM_ATK_MIN = 0, ANIM_ATK_MAX = 1;        // beat-reaction attack (in) — beats
const ANIM_REL_MIN = 0.02, ANIM_REL_MAX = 4;     // beat-reaction release (out) — beats
const ANIM_EASE_MIN = 0.3, ANIM_EASE_MAX = 4;    // ease exponent (accel in / decel out)
const ANIM_STAG_MIN = 0, ANIM_STAG_MAX = 3;      // reaction distribution stagger — beats

/** Snap `raw` to the nearest of `points` when within `tolerance`, else leave it
 *  as-is — lets a slider drag "catch" on common values (45°/90°/etc, 0, whole
 *  half-steps of scale) without losing free dragging everywhere else. */
function snapTo(raw: number, points: number[], tolerance: number): number {
  for (const p of points) if (Math.abs(raw - p) <= tolerance) return p;
  return raw;
}
// Rotation snaps to the compass points every 45° (covers 45/90/180 and, since
// the range wraps at ±180, the 360 the user asked for is the same position).
const ROT_SNAP_POINTS = [-180, -135, -90, -45, 0, 45, 90, 135, 180];
const ROT_SNAP_TOL = 4;
// Position (Offset/Move X/Y/Z) only snaps back to centred — 0.
const POS_SNAP_POINTS = [0];
const POS_SNAP_TOL = 0.05;
// Scale snaps to every half-step.
const SCALE_SNAP_POINTS = [0.5, 1, 1.5, 2, 2.5, 3, 4, 5, 6, 7, 8, 9, 10];
const SCALE_SNAP_TOL = 0.035;

const SHAPE_LABEL: Record<ObstacleShape, string> = {
  regular: 'Regular', diagonal: 'Diagonal', center: 'Center', square: 'Square', triangle: 'Triangle',
};
const CURVE_LABEL: Record<DesignCurve, string> = {
  line: 'Straight line', arc: 'Arc', valley: 'Valley', 's-curve': 'S-curve',
  wave: 'Wave', ripple: 'Ripple', zigzag: 'Zigzag', switchback: 'Switchback',
  saw: 'Sawtooth', steps: 'Steps', peak: 'Peak', bell: 'Bell',
  circle: 'Circle', spiral: 'Spiral', custom: 'Custom path',
};
// The Curve dropdown, grouped so the 15 presets read clearly: bent lines, then
// the parametric 2D shapes, then the user-drawn node path.
const CURVE_GROUPS: { label: string; curves: DesignCurve[] }[] = [
  { label: 'Lines', curves: ['line', 'arc', 'valley', 's-curve', 'wave', 'ripple', 'zigzag', 'switchback', 'saw', 'steps', 'peak', 'bell'] },
  { label: 'Shapes', curves: ['circle', 'spiral'] },
  { label: 'Custom', curves: ['custom'] },
];
const MIRROR_LABEL: Record<DesignMirror, string> = {
  none: 'None', x: 'Mirror X', y: 'Mirror Y', kaleidoscope: 'Kaleidoscope',
};
const ANIM_LABEL: Record<DesignAnimPreset, string> = {
  none: 'None', pulse: 'Pulse', breathe: 'Breathe', shockwave: 'Shockwave', flicker: 'Flicker',
  wave: 'Wave', tentacle: 'Tentacle', ripple: 'Ripple', jelly: 'Jelly',
  spin: 'Spin', orbit: 'Orbit', twist: 'Twist', sway: 'Sway',
};
const ANIM_DESC: Record<DesignAnimPreset, string> = {
  none: '',
  pulse: 'Scales up on each beat / hit — punchy.',
  breathe: 'Slow, gentle scale in and out.',
  shockwave: 'A ring of scale sweeps outward from the centre.',
  flicker: 'Nervous shimmer that flares with the signal.',
  wave: 'Walls undulate in a travelling sine.',
  tentacle: 'Whips side to side, or opens & closes from the centre (see Motion).',
  ripple: 'Concentric ripples push outward from the centre.',
  jelly: 'Gelatinous wobble — scale + shear with a lag.',
  spin: 'Each wall spins in place.',
  orbit: 'The whole art revolves around its centre.',
  twist: 'Winds and unwinds like a ribbon along its length.',
  sway: 'Tilts gently back and forth, like wind.',
};
const ANIM_DIST_LABEL: Record<DesignAnimDist, string> = {
  uniform: 'Uniform (all together)', random: 'Random (scatter)', sequence: 'Sequence (sweep)', radial: 'Radial (from center)',
};

export default function ShapeDesignerPanel() {
  const open = useEditorStore(s => s.shapeDesignerOpen);
  const setOpen = useEditorStore(s => s.setShapeDesignerOpen);
  const designShapes = useEditorStore(s => s.designShapes);
  const selectedIds = useEditorStore(s => s.selectedDesignShapeIds);
  const addDesignShape = useEditorStore(s => s.addDesignShape);
  const updateDesignShape = useEditorStore(s => s.updateDesignShape);
  const updateDesignShapes = useEditorStore(s => s.updateDesignShapes);
  const removeDesignShape = useEditorStore(s => s.removeDesignShape);
  const removeDesignShapes = useEditorStore(s => s.removeDesignShapes);
  const duplicateDesignShape = useEditorStore(s => s.duplicateDesignShape);
  const selectDesignShape = useEditorStore(s => s.selectDesignShape);
  const setCurrentTime = useEditorStore(s => s.setCurrentTime);
  const beatmap = useEditorStore(s => s.beatmap);
  // Current playhead beat, rounded to 1/4 beat so the panel only re-renders a
  // few times per beat (not 60fps) — enough to highlight the wall at the trigger.
  const playBeat = useEditorStore(s => Math.round(s.playback.currentBeat * 4) / 4);
  // Which shape (primary or a part) is being edited — lives in the STORE (not
  // local state) so the 3D scene's move-gizmo knows which one to show. Entering
  // a wall art (selectDesignShape/setSelectedDesignShapeIds) already lands this on the
  // primary shape.
  const expandedPartId = useEditorStore(s => s.activeDesignMeshId);
  const setExpandedPartId = useEditorStore(s => s.setActiveDesignMeshId);
  // Custom-curve control nodes (the draggable path). The scene shows the gizmo
  // handles; this panel selects/adds/removes nodes and nudges them precisely.
  const activeNodeIndex = useEditorStore(s => s.activeDesignNodeIndex);
  const setActiveNodeIndex = useEditorStore(s => s.setActiveDesignNodeIndex);
  const addDesignNode = useEditorStore(s => s.addDesignNode);
  const removeDesignNode = useEditorStore(s => s.removeDesignNode);
  const updateDesignNode = useEditorStore(s => s.updateDesignNode);
  // My Library — cross-project saved wall arts.
  const designLibrary = useEditorStore(s => s.designLibrary);
  const loadDesignLibraryFromServer = useEditorStore(s => s.loadDesignLibraryFromServer);
  const addToDesignLibrary = useEditorStore(s => s.addToDesignLibrary);
  const removeFromDesignLibrary = useEditorStore(s => s.removeFromDesignLibrary);
  const setLibraryShapeVisibility = useEditorStore(s => s.setLibraryShapeVisibility);
  const addLibraryArtToMap = useEditorStore(s => s.addLibraryArtToMap);
  const { panelRef, headerProps, draggedStyle } = useDraggablePanel();
  // In the list, the first click on a row jumps the playhead to that wall (and
  // focuses the row); a second click on the focused row opens its editor.
  const [focusedId, setFocusedId] = useState<string | null>(null);
  // List-view tab: this project's wall arts, the user's own cross-project
  // library, or the shared public library.
  const [libTab, setLibTab] = useState<'project' | 'library' | 'public'>('project');
  // Brief "Saved ✓" flash on the Save-to-Library button.
  const [savedFlash, setSavedFlash] = useState(false);
  // Keep the Meshes list pinned (sticky) at the top of the panel while scrolling
  // the long mesh properties, so you can switch meshes without scrolling up. The
  // pin toggle lives at the right of the "Meshes" header. On by default.
  const [meshesPinned, setMeshesPinned] = useState(true);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  // Refresh the server-backed library (and run the one-time migration) whenever
  // the panel opens, so "My Library" / "Public" reflect the latest server state.
  useEffect(() => {
    if (open) void loadDesignLibraryFromServer();
  }, [open, loadDesignLibraryFromServer]);

  if (!open) return null;

  // Two views: the LIST (default on open) and the per-wall EDITOR (once a wall is
  // entered — click a row, click/double-click a wall in the scene, or box-select).
  // The editor shows the PRIMARY (last-selected) wall; edits apply to EVERY
  // selected wall so a box-select can be tuned in one go.
  const g = designShapes.find(d => d.id === selectedIds[selectedIds.length - 1]) ?? null;
  const inEditor = selectedIds.length > 0 && !!g;
  const multi = selectedIds.length > 1;
  const patch = (u: Partial<DesignShape>) => { if (selectedIds.length) updateDesignShapes(selectedIds, u); };
  // Split the server-backed library by ownership: "My Library" = the caller's
  // own shapes; "Public" = public shapes owned by someone else. Legacy entries
  // (no ownership metadata yet) fall into "My Library".
  const myShapes = designLibrary.filter(s => s.is_owner !== false);
  const publicShapes = designLibrary.filter(s => s.is_owner === false && s.visibility === 'public');
  // Manual entry: parse a typed value, clamp to the slider's range (no snapping —
  // manual means exact), round, and apply. Shared by every GradientSlider's
  // editable readout so you can type an exact number instead of dragging.
  const commitNum = (raw: string, min: number, max: number, decimals: number, apply: (n: number) => void) => {
    const n = parseFloat(raw);
    if (!Number.isFinite(n)) return;
    apply(round(Math.max(min, Math.min(max, n)), decimals));
  };

  // ── Frequency/Effects/Expand target ──────────────────────────────────────
  // When the shapes are DISCONNECTED (shapesIndependent) and a PART is selected,
  // those sections edit THAT part's own override fields (falling back to the
  // group's value when unset). Otherwise (anchored, or primary selected) they
  // edit the group — shared by every shape. `fq` reads, `fqSet` writes.
  const freqSelPart = (g && expandedPartId && expandedPartId !== PRIMARY_MESH_ID)
    ? (g.parts || []).find(p => p.id === expandedPartId) || null : null;
  const freqPart = (g && g.shapesIndependent) ? freqSelPart : null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fq = (k: string): any => (freqPart && (freqPart as any)[k] != null) ? (freqPart as any)[k] : (g as any)?.[k];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fqSet = (u: Record<string, any>) => {
    if (!g) return;
    if (freqPart) updateDesignShape(g.id, { parts: (g.parts || []).map(p => (p.id === freqPart.id ? { ...p, ...u } : p)) });
    else patch(u);
  };

  // beat → song time (mm:ss). Design beats are in the offset-adjusted chart frame
  // (same as playback.currentBeat), so audio time = beat*60/bpm + offset(s).
  const bpm = beatmap?.bpm || 120;
  const offsetSec = (beatmap?.offset || 0) / 1000;
  const fmtTime = (beat: number) => {
    const s = Math.max(0, (beat * 60) / bpm + offsetSec);
    const m = Math.floor(s / 60);
    return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  };
  const wallLabel = (d: DesignShape) => {
    if ((d.name || '').trim()) return d.name.trim();
    const extra = (d.parts || []).length;
    const shapeTag = extra > 0 ? `${SHAPE_LABEL[d.shape]} +${extra}` : SHAPE_LABEL[d.shape];
    return `${shapeTag} · ×${countDesignMeshes(d)}`;
  };

  // First click on a row → jump the playhead to the wall's start + focus it;
  // click again on the focused row → open its editor.
  const handleRowClick = (d: DesignShape) => {
    if (focusedId === d.id) {
      selectDesignShape(d.id);   // second click → enter editor
      return;
    }
    setFocusedId(d.id);
    const startBeat = designBeatRange(d)[0];
    setCurrentTime(Math.max(0, (startBeat * 60) / bpm + offsetSec));   // jump to it
  };

  return (
    <div ref={panelRef} style={{ ...panelStyle, ...draggedStyle }}>
      <div style={{ ...headerStyle, ...headerProps.style }} onPointerDown={headerProps.onPointerDown}>
        <span style={titleStyle}>
          {inEditor && (
            <button
              style={circleHeaderBtnStyle}
              onClick={() => { selectDesignShape(null); setFocusedId(null); setExpandedPartId(null); }}
              title="Back to Shapes list"
            >
              <IcChevLeft size={16} />
            </button>
          )}
          {inEditor ? (multi ? `${selectedIds.length} shapes` : wallLabel(g!)) : 'Shapes'}
        </span>
        <button style={circleHeaderBtnStyle} onClick={() => setOpen(false)} title="Close (Esc)"><IcClose size={16} /></button>
      </div>

      {!beatmap ? (
        <div style={hintStyle}>Load a map to add decorative walls.</div>
      ) : !g ? (
        /* ══ LIST VIEW — tabs: this project's arts + the cross-project library ══ */
        <>
          <Segmented<'project' | 'library' | 'public'>
            options={[['project', 'Project'], ['library', `My Library (${myShapes.length})`], ['public', `Public (${publicShapes.length})`]]}
            value={libTab}
            onChange={setLibTab}
          />
          {libTab === 'project' ? (
          <>
          <div style={{ ...emptyStyle, textAlign: 'left', padding: '0 2px 4px' }}>
            {designShapes.length === 0
              ? 'No shapes yet. Add one to start.'
              : 'Click a shape to jump to it; click again to edit.'}
          </div>
          <div style={listStyle}>
            {[...designShapes].sort((a, b) => a.beat - b.beat).map(d => {
              const [b0, b1] = designBeatRange(d);
              // Highlight the wall that's at the playhead right now (visible /
              // touching the trigger), with a ±0.5-beat tolerance so single-beat
              // (x/y) walls light up as the trigger passes them.
              const atPlayhead = playBeat >= b0 - 0.5 && playBeat <= b1 + 0.5;
              return (
                <div
                  key={d.id}
                  style={{
                    ...rowStyle(focusedId === d.id),
                    ...(atPlayhead ? { boxShadow: 'inset 3px 0 0 var(--accent)' } : null),
                  }}
                  onClick={() => handleRowClick(d)}
                >
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: d.color, flexShrink: 0, boxShadow: '0 0 6px ' + d.color }} />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {wallLabel(d)}
                  </span>
                  <span style={{ color: color.inkFaint, fontSize: 10, whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {fmtTime(b0)}–{fmtTime(b1)}
                  </span>
                  <button
                    style={rowDuplicateStyle}
                    title="Duplicate this shape"
                    onClick={e => { e.stopPropagation(); duplicateDesignShape(d.id, `${wallLabel(d)} (Copy)`); }}
                  >
                    <IcCopy size={13} />
                  </button>
                  <button
                    style={rowTrashStyle}
                    title="Delete this shape"
                    onClick={e => { e.stopPropagation(); removeDesignShape(d.id); }}
                  >
                    <IcTrash size={13} />
                  </button>
                </div>
              );
            })}
          </div>

          <button style={addBtnStyle} onClick={addDesignShape}>
            <IcPlus size={13} /> New Shape
          </button>
          </>
          ) : libTab === 'library' ? (
          /* ── MY LIBRARY — the caller's own cross-project saved wall arts ── */
          <>
          <div style={{ ...emptyStyle, textAlign: 'left', padding: '0 2px 4px' }}>
            {myShapes.length === 0
              ? 'Empty. Open a shape and hit "Save to Library" to reuse it in any project.'
              : 'Click a saved art to add it to this project.'}
          </div>
          <div style={listStyle}>
            {myShapes.map(saved => {
              const isPublic = saved.visibility === 'public';
              return (
              <div key={saved.id} style={rowStyle(false)} onClick={() => { addLibraryArtToMap(saved.id); setLibTab('project'); }} title="Add to this project">
                <span style={{ width: 12, height: 12, borderRadius: 3, background: saved.group.color, flexShrink: 0, boxShadow: '0 0 6px ' + saved.group.color }} />
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {saved.name || wallLabel(saved.group)}
                </span>
                {isPublic && <span style={publicBadgeStyle}>Public</span>}
                <button
                  style={rowDuplicateStyle}
                  title={isPublic ? 'Make private' : 'Publish — share with everyone'}
                  onClick={e => { e.stopPropagation(); void setLibraryShapeVisibility(saved.id, !isPublic); }}
                >
                  {isPublic ? <IcEye size={13} /> : <IcEyeOff size={13} />}
                </button>
                <button style={rowDuplicateStyle} title="Add to this project" onClick={e => { e.stopPropagation(); addLibraryArtToMap(saved.id); setLibTab('project'); }}>
                  <IcPlus size={13} />
                </button>
                <button style={rowTrashStyle} title="Remove from library" onClick={e => { e.stopPropagation(); removeFromDesignLibrary(saved.id); }}>
                  <IcTrash size={13} />
                </button>
              </div>
              );
            })}
          </div>
          </>
          ) : (
          /* ── PUBLIC — shapes shared by other users (read-only; add to map) ── */
          <>
          <div style={{ ...emptyStyle, textAlign: 'left', padding: '0 2px 4px' }}>
            {publicShapes.length === 0
              ? 'No public shapes yet.'
              : 'Click a shared art to add it to this project.'}
          </div>
          <div style={listStyle}>
            {publicShapes.map(saved => (
              <div key={saved.id} style={rowStyle(false)} onClick={() => { addLibraryArtToMap(saved.id); setLibTab('project'); }} title="Add to this project">
                <span style={{ width: 12, height: 12, borderRadius: 3, background: saved.group.color, flexShrink: 0, boxShadow: '0 0 6px ' + saved.group.color }} />
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {saved.name || wallLabel(saved.group)}
                </span>
                {saved.owner && <span style={{ color: color.inkFaint, fontSize: 10, whiteSpace: 'nowrap', flexShrink: 0, maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis' }}>{saved.owner}</span>}
                <button style={rowDuplicateStyle} title="Add to this project" onClick={e => { e.stopPropagation(); addLibraryArtToMap(saved.id); setLibTab('project'); }}>
                  <IcPlus size={13} />
                </button>
              </div>
            ))}
          </div>
          </>
          )}
        </>
      ) : (
        /* ══ EDITOR VIEW — the entered wall (Model / Frequency / Effects / Position) ══ */
        <>
            <div style={editorStyle}>
              {/* Save this art to the cross-project library (the "My Library" tab).
                  Uses the wall art's Name — set it above first for a tidy library. */}
              {!multi && (
                <button
                  style={{ ...dupBtnStyle, marginTop: 0 }}
                  onClick={() => { addToDesignLibrary(g.id); setSavedFlash(true); window.setTimeout(() => setSavedFlash(false), 1400); }}
                  title="Save this shape to your library — reuse it in any project"
                >
                  <IcStar size={13} /> {savedFlash ? 'Saved to Library ✓' : 'Save to Library'}
                </button>
              )}
              {/* ══ MODEL — the shape's name + the list of meshes it's made
                  of. The primary mesh and every extra mesh are edited the same
                  way — a row that expands into Obstacle/Color/Scale/Rotation
                  (+ Offset for extras, since they sit beside the primary). ══ */}
              {!multi && (
                <>
                  <FieldLabel>Name</FieldLabel>
                  <input
                    value={g.name || ''}
                    placeholder={`${SHAPE_LABEL[g.shape]} · ×${countDesignMeshes(g)}`}
                    onChange={e => updateDesignShape(g.id, { name: e.target.value })}
                    style={inputStyle}
                  />

                  {/* MESHES section — optionally sticky (pinned) to the panel top so
                      you can switch meshes while scrolling the long properties below.
                      The wrapper is always a flex column (preserves the 9px row gaps);
                      pinning just adds position:sticky + an opaque backdrop. */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 9, ...(meshesPinned ? stickyMeshesStyle : null) }}>
                  <div style={{ ...sectionHeadStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...(meshesPinned ? { marginTop: 0, borderTop: 'none', paddingTop: 4 } : null) }}>
                    <span>Meshes</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button
                        type="button"
                        style={addMeshMiniStyle}
                        title="Add a mesh"
                        onClick={() => {
                          const existingParts = g.parts || [];
                          const used = new Set([g.shape, ...existingParts.map(p => p.shape)]);
                          const nextShape = DESIGN_MESHES.find(s => !used.has(s)) ?? DESIGN_MESHES[(existingParts.length + 1) % DESIGN_MESHES.length];
                          // Every added mesh gets a distinct neon colour so it stands
                          // out from the others — unless Colors are combined, where it
                          // stays linked to the primary.
                          const meshColor = g.linkedColor
                            ? g.color
                            : distinctNeonColor([g.color, ...existingParts.map(p => p.color)]);
                          const part = { ...makeDesignMesh(nextShape, meshColor), style: g.style ?? 'solid' };
                          updateDesignShape(g.id, { parts: [...(g.parts || []), part] }, true);
                          setExpandedPartId(part.id);
                        }}
                      >
                        <IcPlus size={11} /> Add mesh
                      </button>
                      <button
                        type="button"
                        onClick={() => setMeshesPinned(v => !v)}
                        title={meshesPinned ? 'Meshes pinned to the top while scrolling — click to unpin' : 'Pin the Meshes list to the top while scrolling'}
                        style={pinBtnStyle(meshesPinned)}
                      >
                        <IcPin size={13} />
                      </button>
                    </div>
                  </div>

                  {/* Combine: two toggles.
                      • Shapes (anchor): ANCHORED (default, locked) = every shape
                        shares ONE Frequency/Effects/Expand and rides as one
                        composite. DISCONNECTED (unlocked) = each shape gets its
                        OWN Frequency/Effects/Expand (edited per selected shape).
                        Scale/Rotation/Offset are per-shape either way.
                      • Colors: share the Primary's Color/Gradient with every part. */}
                  {(g.parts || []).length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: color.inkFaint, fontSize: 11, fontWeight: 600, flexShrink: 0 }}>Combine</span>
                      <div style={{ display: 'flex', gap: 6, flex: 1 }}>
                        <button
                          onClick={() => updateDesignShape(g.id, { shapesIndependent: !g.shapesIndependent }, true)}
                          title={!g.shapesIndependent
                            ? 'Meshes anchored — all share one Frequency/Effects/Expand. Click to disconnect (per-mesh).'
                            : 'Meshes disconnected — each mesh has its own Frequency/Effects/Expand. Click to anchor them together.'}
                          style={combineBtnStyle(!g.shapesIndependent)}
                        >
                          {!g.shapesIndependent ? <IcLock size={11} /> : <IcUnlock size={11} />} Meshes
                        </button>
                        <button
                          onClick={() => {
                            // force=true — a discrete, drastic change (restyles every
                            // part at once) so it always gets its own undo step.
                            if (g.linkedColor) { updateDesignShape(g.id, { linkedColor: false }, true); return; }
                            const styled = (g.parts || []).map(p => ({
                              ...p, color: g.color, gradient: g.gradient, colorEnd: g.colorEnd,
                            }));
                            updateDesignShape(g.id, { linkedColor: true, parts: styled }, true);
                          }}
                          title={g.linkedColor ? 'Colors combined — Color/Gradient shared. Click to edit independently.' : 'Combine Colors: share the Primary\'s Color/Gradient with every mesh.'}
                          style={combineBtnStyle(g.linkedColor)}
                        >
                          {g.linkedColor ? <IcLock size={11} /> : <IcUnlock size={11} />} Colors
                        </button>
                      </div>
                    </div>
                  )}

                  {/* The primary shape — part #1. With no extra parts it's the
                      wall art's only shape and can't be deleted (a "Primary" tag
                      stands in for the trash icon). Once there's at least one
                      extra part, the primary becomes deletable too: removing it
                      PROMOTES the next part to primary, so the wall art always
                      keeps exactly one shape. Clicking a row just SELECTS it
                      (highlighted) — its fields are edited in the block below,
                      not inline, so Parts stays a plain view/combine list. */}
                  {(() => {
                    const active = expandedPartId === PRIMARY_MESH_ID;
                    const parts = g.parts || [];
                    const deletePrimary = () => {
                      if (parts.length === 0) return;
                      const [promoted, ...rest] = parts;
                      updateDesignShape(g.id, {
                        shape: promoted.shape, color: promoted.color,
                        scale: promoted.scale, scaleZ: promoted.scaleZ ?? 1, rotation: promoted.rotation,
                        rotationX: promoted.rotationX || 0, rotationY: promoted.rotationY || 0,
                        gradient: promoted.gradient || false, colorEnd: promoted.colorEnd || '#00E5FF',
                        style: promoted.style ?? 'solid',
                        parts: rest,
                      }, true);
                      setExpandedPartId(PRIMARY_MESH_ID);
                    };
                    // Duplicate the primary's shape/style as a new PART (the
                    // primary itself is unique — a wall art always has exactly
                    // one — so "duplicating" it adds a copy alongside it).
                    const duplicatePrimary = () => {
                      const newPart: DesignMesh = {
                        ...makeDesignMesh(g.shape, g.color),
                        gradient: g.gradient, colorEnd: g.colorEnd, style: g.style ?? 'solid',
                        scale: g.scale, scaleZ: g.scaleZ ?? 1, rotation: g.rotation,
                        rotationX: g.rotationX || 0, rotationY: g.rotationY || 0,
                      };
                      updateDesignShape(g.id, { parts: [...parts, newPart] }, true);
                      setExpandedPartId(newPart.id);
                    };
                    return (
                      <div style={rowStyle(active, g.hidden)} onClick={() => setExpandedPartId(PRIMARY_MESH_ID)}>
                        <span style={{ width: 12, height: 12, borderRadius: 3, background: g.color, flexShrink: 0, boxShadow: '0 0 6px ' + g.color }} />
                        <span style={{ flex: 1, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 5 }}>
                          {SHAPE_LABEL[g.shape]}
                          {g.linkedColor && <IcLock size={10} style={{ color: color.inkFaint }} />}
                        </span>
                        <button
                          style={rowDuplicateStyle}
                          title={g.hidden ? 'Show this mesh' : 'Hide this mesh'}
                          onClick={e => { e.stopPropagation(); updateDesignShape(g.id, { hidden: !g.hidden }, true); }}
                        >
                          {g.hidden ? <IcEyeOff size={13} /> : <IcEye size={13} />}
                        </button>
                        <button style={rowDuplicateStyle} title="Duplicate this mesh" onClick={e => { e.stopPropagation(); duplicatePrimary(); }}>
                          <IcCopy size={13} />
                        </button>
                        {parts.length === 0 ? (
                          <span style={{ color: color.inkFaint, fontSize: 9, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}>Primary</span>
                        ) : (
                          <button style={rowTrashStyle} title="Remove this mesh" onClick={e => { e.stopPropagation(); deletePrimary(); }}>
                            <IcTrash size={13} />
                          </button>
                        )}
                      </div>
                    );
                  })()}

                  {/* Extra parts — combine more shapes into this ONE wall art;
                      they ride along at the same Frequency/Effects/Position
                      slots as the primary, so the whole thing repeats as a unit. */}
                  {(g.parts || []).map(part => {
                    const active = expandedPartId === part.id;
                    const duplicatePart = () => {
                      const newPart: DesignMesh = {
                        ...makeDesignMesh(part.shape, part.color),
                        gradient: part.gradient, colorEnd: part.colorEnd, style: part.style ?? 'solid',
                        scale: part.scale, scaleZ: part.scaleZ ?? 1, rotation: part.rotation,
                        rotationX: part.rotationX || 0, rotationY: part.rotationY || 0,
                        offsetX: part.offsetX + 0.3, offsetY: part.offsetY, offsetBeat: part.offsetBeat,
                      };
                      updateDesignShape(g.id, { parts: [...(g.parts || []), newPart] }, true);
                      setExpandedPartId(newPart.id);
                    };
                    return (
                      <div key={part.id} style={rowStyle(active, part.hidden)} onClick={() => setExpandedPartId(part.id)}>
                        <span style={{ width: 12, height: 12, borderRadius: 3, background: part.color, flexShrink: 0, boxShadow: '0 0 6px ' + part.color }} />
                        <span style={{ flex: 1, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 5 }}>
                          {SHAPE_LABEL[part.shape]}
                          {g.linkedColor && <IcLock size={10} style={{ color: color.inkFaint }} />}
                        </span>
                        <button
                          style={rowDuplicateStyle}
                          title={part.hidden ? 'Show this mesh' : 'Hide this mesh'}
                          onClick={e => {
                            e.stopPropagation();
                            updateDesignShape(g.id, { parts: (g.parts || []).map(p => (p.id === part.id ? { ...p, hidden: !p.hidden } : p)) }, true);
                          }}
                        >
                          {part.hidden ? <IcEyeOff size={13} /> : <IcEye size={13} />}
                        </button>
                        <button style={rowDuplicateStyle} title="Duplicate this mesh" onClick={e => { e.stopPropagation(); duplicatePart(); }}>
                          <IcCopy size={13} />
                        </button>
                        <button
                          style={rowTrashStyle}
                          title="Remove this mesh"
                          onClick={e => {
                            e.stopPropagation();
                            updateDesignShape(g.id, { parts: (g.parts || []).filter(p => p.id !== part.id) }, true);
                            if (active) setExpandedPartId(PRIMARY_MESH_ID);
                          }}
                        >
                          <IcTrash size={13} />
                        </button>
                      </div>
                    );
                  })}
                  </div>

                  {/* ══ Selected shape's editor — whichever row is active above
                      (Primary or a Part). Kept OUT of the Parts list itself, so
                      Parts stays a plain view/select/combine list. ══ */}
                  {(() => {
                    const isPart = !!(expandedPartId && expandedPartId !== PRIMARY_MESH_ID);
                    const part = isPart ? (g.parts || []).find(p => p.id === expandedPartId) : null;
                    if (isPart && !part) return null;
                    const patchPart = (u: Partial<DesignMesh>) => {
                      if (!part) return;
                      updateDesignShape(g.id, { parts: (g.parts || []).map(p => (p.id === part.id ? { ...p, ...u } : p)) });
                    };
                    // ONLY colour ever cascades (when Combine·Colors is on). Scale /
                    // Scale Z / Rotation stay per-shape ALWAYS — each shape keeps its
                    // own size/orientation so the composite is many distinct shapes.
                    const patchPrimaryStyle = (u: { color?: string; gradient?: boolean; colorEnd?: string; scale?: number; scaleZ?: number; rotation?: number; rotationX?: number; rotationY?: number }) => {
                      const isColor = 'color' in u || 'gradient' in u || 'colorEnd' in u;
                      if (isColor && g.linkedColor) {
                        updateDesignShape(g.id, { ...u, parts: (g.parts || []).map(p => ({ ...p, ...u })) });
                      } else patch(u);
                    };
                    const gradientOn = part ? part.gradient : g.gradient;
                    const colorEndVal = (part ? part.colorEnd : g.colorEnd) || '#00E5FF';
                    const colorLocked = !!(part && g.linkedColor);
                    return (
                      <div style={partEditorStyle}>
                        <FieldLabel>{part ? 'Mesh (extra)' : 'Mesh (primary)'}</FieldLabel>
                        <select
                          value={part ? part.shape : g.shape}
                          onChange={e => (part ? patchPart({ shape: e.target.value as ObstacleShape }) : patch({ shape: e.target.value as ObstacleShape }))}
                          style={selectStyle}
                        >
                          {DESIGN_MESHES.map(s => <option key={s} value={s}>{SHAPE_LABEL[s]}</option>)}
                        </select>

                        {/* Render style — PER SHAPE, so one art can mix solid +
                            wireframe. Solid = filled glowing panel; Wireframe =
                            glowing edges only (the game's two wall looks). */}
                        {(() => {
                          const shapeStyle = (part ? part.style : g.style) ?? 'solid';
                          const setStyle = (v: DesignShape['style']) => (part ? patchPart({ style: v }) : patch({ style: v }));
                          return (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={() => setStyle('solid')} title="Solid — filled glowing panel." style={combineBtnStyle(shapeStyle === 'solid')}>Solid</button>
                              <button onClick={() => setStyle('wireframe')} title="Wireframe — glowing edges only." style={combineBtnStyle(shapeStyle === 'wireframe')}>Wireframe</button>
                            </div>
                          );
                        })()}

                        {/* ── Shape group (Scale/Rotation X/Y/Z) — ALWAYS per-shape,
                            never combined: each shape keeps its own size/orientation. ── */}
                        {(
                          <>
                            <GradientSlider
                              inline labelWidth={LABEL_W} label="Scale"
                              value={((part ? part.scale : g.scale) - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)}
                              onChange={v => {
                                const raw = SCALE_MIN + v * (SCALE_MAX - SCALE_MIN);
                                const val = round(snapTo(raw, SCALE_SNAP_POINTS, SCALE_SNAP_TOL), 2);
                                part ? patchPart({ scale: val }) : patchPrimaryStyle({ scale: val });
                              }}
                              valueText={(part ? part.scale : g.scale).toFixed(2)}
                              onCommitText={raw => commitNum(raw, SCALE_MIN, SCALE_MAX, 2, val => part ? patchPart({ scale: val }) : patchPrimaryStyle({ scale: val }))}
                            />

                            <GradientSlider
                              inline labelWidth={LABEL_W} label="Scale Z"
                              value={(((part ? part.scaleZ : g.scaleZ) ?? 1) - SCALEZ_MIN) / (SCALEZ_MAX - SCALEZ_MIN)}
                              onChange={v => {
                                const raw = SCALEZ_MIN + v * (SCALEZ_MAX - SCALEZ_MIN);
                                const val = round(snapTo(raw, SCALE_SNAP_POINTS, SCALE_SNAP_TOL), 2);
                                part ? patchPart({ scaleZ: val }) : patchPrimaryStyle({ scaleZ: val });
                              }}
                              valueText={((part ? part.scaleZ : g.scaleZ) ?? 1).toFixed(2)}
                              onCommitText={raw => commitNum(raw, SCALEZ_MIN, SCALEZ_MAX, 2, val => part ? patchPart({ scaleZ: val }) : patchPrimaryStyle({ scaleZ: val }))}
                            />

                            <GradientSlider
                              inline labelWidth={LABEL_W} label="Rotation Z"
                              value={((part ? part.rotation : g.rotation) - ROT_MIN) / (ROT_MAX - ROT_MIN)}
                              onChange={v => {
                                const raw = ROT_MIN + v * (ROT_MAX - ROT_MIN);
                                const val = Math.round(snapTo(raw, ROT_SNAP_POINTS, ROT_SNAP_TOL));
                                part ? patchPart({ rotation: val }) : patchPrimaryStyle({ rotation: val });
                              }}
                              valueText={`${Math.round(part ? part.rotation : g.rotation)}°`}
                              onCommitText={raw => commitNum(raw, ROT_MIN, ROT_MAX, 0, val => part ? patchPart({ rotation: val }) : patchPrimaryStyle({ rotation: val }))}
                            />

                            <GradientSlider
                              inline labelWidth={LABEL_W} label="Rotation X"
                              value={((part ? part.rotationX || 0 : g.rotationX || 0) - ROT_MIN) / (ROT_MAX - ROT_MIN)}
                              onChange={v => {
                                const raw = ROT_MIN + v * (ROT_MAX - ROT_MIN);
                                const val = Math.round(snapTo(raw, ROT_SNAP_POINTS, ROT_SNAP_TOL));
                                part ? patchPart({ rotationX: val }) : patchPrimaryStyle({ rotationX: val });
                              }}
                              valueText={`${Math.round(part ? part.rotationX || 0 : g.rotationX || 0)}°`}
                              onCommitText={raw => commitNum(raw, ROT_MIN, ROT_MAX, 0, val => part ? patchPart({ rotationX: val }) : patchPrimaryStyle({ rotationX: val }))}
                            />

                            <GradientSlider
                              inline labelWidth={LABEL_W} label="Rotation Y"
                              value={((part ? part.rotationY || 0 : g.rotationY || 0) - ROT_MIN) / (ROT_MAX - ROT_MIN)}
                              onChange={v => {
                                const raw = ROT_MIN + v * (ROT_MAX - ROT_MIN);
                                const val = Math.round(snapTo(raw, ROT_SNAP_POINTS, ROT_SNAP_TOL));
                                part ? patchPart({ rotationY: val }) : patchPrimaryStyle({ rotationY: val });
                              }}
                              valueText={`${Math.round(part ? part.rotationY || 0 : g.rotationY || 0)}°`}
                              onCommitText={raw => commitNum(raw, ROT_MIN, ROT_MAX, 0, val => part ? patchPart({ rotationY: val }) : patchPrimaryStyle({ rotationY: val }))}
                            />
                          </>
                        )}

                        {/* ── Offset X/Y/Z — local position of THIS shape (primary or
                            part) within the composite, so shape 1 can be nudged /
                            aligned just like the extra parts (e.g. to centre a
                            mirror). Primary → g.offset*, part → part.offset*. ── */}
                        {(() => {
                          const offX = part ? part.offsetX : (g.offsetX || 0);
                          const offY = part ? part.offsetY : (g.offsetY || 0);
                          const offZ = part ? part.offsetBeat : (g.offsetZ || 0);
                          const setOffX = (val: number) => part ? patchPart({ offsetX: val }) : patch({ offsetX: val });
                          const setOffY = (val: number) => part ? patchPart({ offsetY: val }) : patch({ offsetY: val });
                          const setOffZ = (val: number) => part ? patchPart({ offsetBeat: val }) : patch({ offsetZ: val });
                          return (
                            <>
                              <GradientSlider
                                inline labelWidth={LABEL_W} label="Offset X"
                                value={(offX - ORG_MIN) / (ORG_MAX - ORG_MIN)}
                                onChange={v => setOffX(round(snapTo(ORG_MIN + v * (ORG_MAX - ORG_MIN), POS_SNAP_POINTS, POS_SNAP_TOL), 2))}
                                valueText={offX.toFixed(2)}
                                onCommitText={raw => commitNum(raw, ORG_MIN, ORG_MAX, 2, setOffX)}
                              />
                              <GradientSlider
                                inline labelWidth={LABEL_W} label="Offset Y"
                                value={(offY - ORG_MIN) / (ORG_MAX - ORG_MIN)}
                                onChange={v => setOffY(round(snapTo(ORG_MIN + v * (ORG_MAX - ORG_MIN), POS_SNAP_POINTS, POS_SNAP_TOL), 2))}
                                valueText={offY.toFixed(2)}
                                onCommitText={raw => commitNum(raw, ORG_MIN, ORG_MAX, 2, setOffY)}
                              />
                              <GradientSlider
                                inline labelWidth={LABEL_W} label="Offset Z"
                                value={(offZ - OFFSET_Z_MIN) / (OFFSET_Z_MAX - OFFSET_Z_MIN)}
                                onChange={v => setOffZ(round(snapTo(OFFSET_Z_MIN + v * (OFFSET_Z_MAX - OFFSET_Z_MIN), POS_SNAP_POINTS, POS_SNAP_TOL), 2))}
                                valueText={offZ.toFixed(2)}
                                onCommitText={raw => commitNum(raw, OFFSET_Z_MIN, OFFSET_Z_MAX, 2, setOffZ)}
                              />
                            </>
                          );
                        })()}

                        {/* ── Color group (Color/Gradient/End) — kept LAST, just above
                            Frequency; locked when "Colors" is combined. ── */}
                        {colorLocked ? (
                          <div style={hintStyle}>Color / Gradient are combined with the Primary — toggle off Combine · Colors to edit independently.</div>
                        ) : (
                          <>
                            {/* Colour mode switch FIRST: Solid (one colour) vs
                                Gradient (Start→End fade). 'on' = gradient. */}
                            <div style={colorRowStyle}>
                              <span style={{ color: color.inkFaint, fontSize: 11, width: LABEL_W }}>Gradient</span>
                              <Segmented<'off' | 'on'>
                                options={[['off', 'Solid'], ['on', 'Gradient']]}
                                value={gradientOn ? 'on' : 'off'}
                                onChange={v => (part ? patchPart({ gradient: v === 'on' }) : patchPrimaryStyle({ gradient: v === 'on' }))}
                              />
                            </div>

                            {/* Then the colour(s) below. */}
                            <div style={colorRowStyle}>
                              <span style={{ color: color.inkFaint, fontSize: 11, width: LABEL_W }}>{gradientOn ? 'Start' : 'Color'}</span>
                              <input
                                type="color"
                                value={part ? part.color : g.color}
                                onChange={e => (part ? patchPart({ color: e.target.value }) : patchPrimaryStyle({ color: e.target.value }))}
                                style={colorInputStyle}
                              />
                              <span style={{ color: color.inkFaint, fontSize: 10, fontFamily: 'ui-monospace, monospace' }}>{part ? part.color : g.color}</span>
                            </div>
                            {gradientOn && (
                              <>
                                <div style={colorRowStyle}>
                                  <span style={{ color: color.inkFaint, fontSize: 11, width: LABEL_W }}>End</span>
                                  <input
                                    type="color"
                                    value={colorEndVal}
                                    onChange={e => (part ? patchPart({ colorEnd: e.target.value }) : patchPrimaryStyle({ colorEnd: e.target.value }))}
                                    style={colorInputStyle}
                                  />
                                  <span style={{ color: color.inkFaint, fontSize: 10, fontFamily: 'ui-monospace, monospace' }}>{colorEndVal}</span>
                                </div>
                                {/* How the gradient distributes the colour across the walls.
                                    Group-level (applies to every gradient in this art). */}
                                <div style={colorRowStyle}>
                                  <span style={{ color: color.inkFaint, fontSize: 11, width: LABEL_W }}>Spread</span>
                                  <select
                                    value={g.gradientDir ?? 'order'}
                                    onChange={e => updateDesignShape(g.id, { gradientDir: e.target.value as DesignGradientDir }, true)}
                                    style={{ ...selectStyle, flex: 1 }}
                                    title="How the gradient distributes its colour across the walls"
                                  >
                                    <option value="order">By order</option>
                                    <option value="x">Along X</option>
                                    <option value="y">Along Y</option>
                                    <option value="z">Along Z · time</option>
                                    <option value="radial">Radial (from center)</option>
                                  </select>
                                </div>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })()}
                </>
              )}

              {/* ══ FREQUENCY — repeat the model into a shaped line/module ══ */}
              <div style={sectionHeadStyle}>Frequency</div>
              {freqPart && <div style={{ ...hintStyle, marginTop: 0, color: 'var(--accent)' }}>Editing THIS mesh's own Frequency / Effects / Expand (meshes disconnected).</div>}
              <FieldLabel>Repeat axis</FieldLabel>
              <MultiSegmented<DesignAxis>
                options={[['x', 'X'], ['y', 'Y'], ['z', 'Z · time']]}
                value={resolveAxes({ axes: fq('axes') })}
                onChange={v => fqSet({ axes: v })}
              />

              <StepperInput
                labelWidth={LABEL_W} label="Count" hoverAccent
                value={String(Math.max(1, Math.round(fq('count'))))}
                onDecrement={() => fqSet({ count: Math.max(1, Math.round(fq('count')) - 1) })}
                onIncrement={() => fqSet({ count: Math.min(200, Math.round(fq('count')) + 1) })}
                onCommitValue={raw => fqSet({ count: Math.max(1, Math.min(200, Math.round(parseFloat(raw)) || 1)) })}
                editTitle="Number of walls (1 = a single obstacle)"
              />

              {/* Spacing steps the walls along the line (or the beat step when a
                  2D shape is extruded on Z). A custom node path owns its own
                  spacing, so it's hidden there. */}
              {fq('curve') !== 'custom' && (
                <GradientSlider
                  inline labelWidth={LABEL_W} label="Spacing"
                  value={(fq('spacing') - SPACE_MIN) / (SPACE_MAX - SPACE_MIN)}
                  onChange={v => fqSet({ spacing: round(SPACE_MIN + v * (SPACE_MAX - SPACE_MIN), 2) })}
                  valueText={fq('spacing').toFixed(2)}
                  onCommitText={raw => commitNum(raw, SPACE_MIN, SPACE_MAX, 2, val => fqSet({ spacing: val }))}
                />
              )}

              <FieldLabel>Curve</FieldLabel>
              <select
                value={fq('curve')}
                onChange={e => {
                  const c = e.target.value as DesignCurve;
                  fqSet({ curve: c });
                  if (c === 'custom') {
                    // Materialise a starter path on the group so nodes appear +
                    // are draggable right away, and select the first node.
                    if (!(g.customPath && g.customPath.length >= 2)) updateDesignShape(g.id, { customPath: makeDefaultCustomPath() }, true);
                    setActiveNodeIndex(0);
                  } else {
                    setActiveNodeIndex(null);
                  }
                }}
                style={selectStyle}
              >
                {CURVE_GROUPS.map(grp => (
                  <optgroup key={grp.label} label={grp.label}>
                    {grp.curves.map(c => <option key={c} value={c}>{CURVE_LABEL[c]}</option>)}
                  </optgroup>
                ))}
              </select>

              {/* Amplitude bends a line; for the 2D shapes it's the radius. A
                  straight line and the custom node path don't use it. */}
              {fq('curve') !== 'line' && fq('curve') !== 'custom' && (
                <GradientSlider
                  inline labelWidth={LABEL_W} label={isShapeCurve(fq('curve')) ? 'Radius' : 'Amplitude'}
                  value={(fq('amplitude') - AMP_MIN) / (AMP_MAX - AMP_MIN)}
                  onChange={v => fqSet({ amplitude: round(AMP_MIN + v * (AMP_MAX - AMP_MIN), 2) })}
                  valueText={fq('amplitude').toFixed(2)}
                  onCommitText={raw => commitNum(raw, AMP_MIN, AMP_MAX, 2, val => fqSet({ amplitude: val }))}
                />
              )}

              {/* ── Custom node path — drag the glowing handles in the 3D scene,
                  or pick a node here and nudge it precisely. Group-level (the
                  path is shared even when shapes are disconnected). ── */}
              {fq('curve') === 'custom' && (() => {
                const pathNodes = (g.customPath && g.customPath.length >= 2) ? g.customPath : DEFAULT_CUSTOM_PATH;
                const sel = (activeNodeIndex != null && activeNodeIndex >= 0 && activeNodeIndex < pathNodes.length) ? activeNodeIndex : null;
                const node = sel != null ? pathNodes[sel] : null;
                return (
                  <div style={partEditorStyle}>
                    <div style={hintStyle}>Drag the glowing handles in the scene to shape the path — grab a sphere to move it, or use its arrows. Pick a node below to nudge it precisely.</div>
                    <div style={{ ...listStyle, maxHeight: 118 }}>
                      {pathNodes.map((nd, i) => (
                        <div key={i} style={rowStyle(i === sel)} onClick={() => setActiveNodeIndex(i)}>
                          <span style={{ flex: 1, textAlign: 'left' }}>Node {i + 1}</span>
                          <span style={{ color: color.inkFaint, fontSize: 10, fontFamily: 'ui-monospace, monospace' }}>
                            {nd.x.toFixed(1)}, {nd.y.toFixed(1)}, {nd.z.toFixed(1)}
                          </span>
                          <button
                            style={{ ...rowTrashStyle, opacity: pathNodes.length <= 2 ? 0.35 : 1 }}
                            title={pathNodes.length <= 2 ? 'A path needs at least 2 nodes' : 'Remove this node'}
                            onClick={e => { e.stopPropagation(); removeDesignNode(g.id, i); }}
                          >
                            <IcTrash size={13} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button style={dupBtnStyle} onClick={() => addDesignNode(g.id)}>
                      <IcPlus size={13} /> Add node
                    </button>
                    {node && (
                      <>
                        <FieldLabel>Node {sel! + 1}</FieldLabel>
                        <GradientSlider
                          inline labelWidth={LABEL_W} label="X"
                          value={(node.x - CUSTOM_XY_MIN) / (CUSTOM_XY_MAX - CUSTOM_XY_MIN)}
                          onChange={v => updateDesignNode(g.id, sel!, { x: round(snapTo(CUSTOM_XY_MIN + v * (CUSTOM_XY_MAX - CUSTOM_XY_MIN), POS_SNAP_POINTS, POS_SNAP_TOL), 2) })}
                          valueText={node.x.toFixed(2)}
                          onCommitText={raw => commitNum(raw, CUSTOM_XY_MIN, CUSTOM_XY_MAX, 2, val => updateDesignNode(g.id, sel!, { x: val }))}
                        />
                        <GradientSlider
                          inline labelWidth={LABEL_W} label="Y"
                          value={(node.y - CUSTOM_XY_MIN) / (CUSTOM_XY_MAX - CUSTOM_XY_MIN)}
                          onChange={v => updateDesignNode(g.id, sel!, { y: round(snapTo(CUSTOM_XY_MIN + v * (CUSTOM_XY_MAX - CUSTOM_XY_MIN), POS_SNAP_POINTS, POS_SNAP_TOL), 2) })}
                          valueText={node.y.toFixed(2)}
                          onCommitText={raw => commitNum(raw, CUSTOM_XY_MIN, CUSTOM_XY_MAX, 2, val => updateDesignNode(g.id, sel!, { y: val }))}
                        />
                        <GradientSlider
                          inline labelWidth={LABEL_W} label="Z · beat"
                          value={(node.z - CUSTOM_Z_MIN) / (CUSTOM_Z_MAX - CUSTOM_Z_MIN)}
                          onChange={v => updateDesignNode(g.id, sel!, { z: round(snapTo(CUSTOM_Z_MIN + v * (CUSTOM_Z_MAX - CUSTOM_Z_MIN), POS_SNAP_POINTS, POS_SNAP_TOL), 2) })}
                          valueText={node.z.toFixed(2)}
                          onCommitText={raw => commitNum(raw, CUSTOM_Z_MIN, CUSTOM_Z_MAX, 2, val => updateDesignNode(g.id, sel!, { z: val }))}
                        />
                      </>
                    )}
                  </div>
                );
              })()}

              <FieldLabel>Tip termination</FieldLabel>
              <Segmented<DesignTip>
                options={[['straight', 'Straight'], ['taper', 'Taper']]}
                value={fq('tip')}
                onChange={v => fqSet({ tip: v })}
              />

              {fq('tip') === 'taper' && (
                <>
                  <GradientSlider
                    inline labelWidth={LABEL_W} label="Taper"
                    value={(fq('taperStrength') - TAP_MIN) / (TAP_MAX - TAP_MIN)}
                    onChange={v => fqSet({ taperStrength: round(TAP_MIN + v * (TAP_MAX - TAP_MIN), 2) })}
                    valueText={`${Math.round(fq('taperStrength') * 100)}%`}
                    onCommitText={raw => commitNum(raw, TAP_MIN * 100, TAP_MAX * 100, 0, pct => fqSet({ taperStrength: round(pct / 100, 2) }))}
                  />
                  {/* How much of each half tapers — 100% = full ramp from centre
                      to the ends; lower = a shorter, sharper tip. */}
                  <GradientSlider
                    inline labelWidth={LABEL_W} label="Taper length"
                    value={((fq('taperLength') ?? 1) - TAPLEN_MIN) / (TAPLEN_MAX - TAPLEN_MIN)}
                    onChange={v => fqSet({ taperLength: round(TAPLEN_MIN + v * (TAPLEN_MAX - TAPLEN_MIN), 2) })}
                    valueText={`${Math.round((fq('taperLength') ?? 1) * 100)}%`}
                    onCommitText={raw => commitNum(raw, TAPLEN_MIN * 100, TAPLEN_MAX * 100, 0, pct => fqSet({ taperLength: round(pct / 100, 2) }))}
                  />
                </>
              )}

              <GradientSlider
                inline labelWidth={LABEL_W} label="Twist"
                value={((fq('twist') ?? 0) - TWIST_MIN) / (TWIST_MAX - TWIST_MIN)}
                onChange={v => fqSet({ twist: Math.round(TWIST_MIN + v * (TWIST_MAX - TWIST_MIN)) })}
                valueText={`${(((fq('twist') ?? 0) / 360)).toFixed(2)}×`}
                onCommitText={raw => commitNum(raw, TWIST_MIN / 360, TWIST_MAX / 360, 2, turns => fqSet({ twist: Math.round(turns * 360) }))}
              />

              {/* ══ EFFECTS — symmetry + array the whole module ══ */}
              <div style={sectionHeadStyle}>Effects</div>
              <Segmented<DesignMirror>
                options={[['none', 'None'], ['x', 'Mirror X'], ['y', 'Mirror Y'], ['kaleidoscope', 'Kaleido']]}
                value={fq('mirror')}
                onChange={v => fqSet({ mirror: v })}
              />
              {fq('mirror') === 'kaleidoscope' && (
                <StepperInput
                  labelWidth={LABEL_W} label="Repeats" hoverAccent
                  value={String(Math.max(2, Math.round(fq('mirrorCount'))))}
                  onDecrement={() => fqSet({ mirrorCount: Math.max(2, Math.round(fq('mirrorCount')) - 1) })}
                  onIncrement={() => fqSet({ mirrorCount: Math.min(24, Math.round(fq('mirrorCount')) + 1) })}
                  onCommitValue={raw => fqSet({ mirrorCount: Math.max(2, Math.min(24, Math.round(parseFloat(raw)) || 2)) })}
                  editTitle="Radial repetitions"
                />
              )}

              {(() => {
                const rc = Math.max(1, Math.round(fq('repeatCount') ?? 1));
                const gap = fq('repeatSpacing') ?? 2;
                const axis = fq('repeatAxis') ?? 'z';
                return (
                  <>
                    <FieldLabel>Repeat module</FieldLabel>
                    <StepperInput
                      labelWidth={LABEL_W} label="Copies" hoverAccent
                      value={String(rc)}
                      onDecrement={() => fqSet({ repeatCount: Math.max(1, rc - 1) })}
                      onIncrement={() => fqSet({ repeatCount: Math.min(50, rc + 1) })}
                      onCommitValue={raw => fqSet({ repeatCount: Math.max(1, Math.min(50, Math.round(parseFloat(raw)) || 1)) })}
                      editTitle="Copies of the whole module"
                    />
                    {rc > 1 && (
                      <>
                        <Segmented<DesignAxis>
                          options={[['x', 'X'], ['y', 'Y'], ['z', 'Z · time']]}
                          value={axis}
                          onChange={v => fqSet({ repeatAxis: v })}
                        />
                        <GradientSlider
                          inline labelWidth={LABEL_W} label="Gap"
                          value={(gap - REP_MIN) / (REP_MAX - REP_MIN)}
                          onChange={v => fqSet({ repeatSpacing: round(REP_MIN + v * (REP_MAX - REP_MIN), 2) })}
                          valueText={gap.toFixed(2)}
                          onCommitText={raw => commitNum(raw, REP_MIN, REP_MAX, 2, val => fqSet({ repeatSpacing: val }))}
                        />
                      </>
                    )}
                  </>
                );
              })()}

              {/* Expand — a PRE-symmetry local offset. With a Mirror/Kaleido
                  active this pushes each reflected copy apart from the group's
                  own centre (the "expand the whole piece" effect that used to
                  live in Global Position by accident); with no symmetry it's
                  just an extra nudge, same as Move. Kept separate from Global
                  Position, which always translates the finished shape rigidly. */}
              <FieldLabel>Expand</FieldLabel>
              <GradientSlider
                inline labelWidth={LABEL_W} label="Expand X"
                value={((fq('spreadX') || 0) - SPREAD_XY_MIN) / (SPREAD_XY_MAX - SPREAD_XY_MIN)}
                onChange={v => {
                  const raw = SPREAD_XY_MIN + v * (SPREAD_XY_MAX - SPREAD_XY_MIN);
                  fqSet({ spreadX: round(snapTo(raw, POS_SNAP_POINTS, POS_SNAP_TOL), 2) });
                }}
                valueText={(fq('spreadX') || 0).toFixed(2)}
                onCommitText={raw => commitNum(raw, SPREAD_XY_MIN, SPREAD_XY_MAX, 2, val => fqSet({ spreadX: val }))}
              />
              <GradientSlider
                inline labelWidth={LABEL_W} label="Expand Y"
                value={((fq('spreadY') || 0) - SPREAD_XY_MIN) / (SPREAD_XY_MAX - SPREAD_XY_MIN)}
                onChange={v => {
                  const raw = SPREAD_XY_MIN + v * (SPREAD_XY_MAX - SPREAD_XY_MIN);
                  fqSet({ spreadY: round(snapTo(raw, POS_SNAP_POINTS, POS_SNAP_TOL), 2) });
                }}
                valueText={(fq('spreadY') || 0).toFixed(2)}
                onCommitText={raw => commitNum(raw, SPREAD_XY_MIN, SPREAD_XY_MAX, 2, val => fqSet({ spreadY: val }))}
              />
              <GradientSlider
                inline labelWidth={LABEL_W} label="Expand Z"
                value={((fq('spreadZ') || 0) - SPREAD_Z_MIN) / (SPREAD_Z_MAX - SPREAD_Z_MIN)}
                onChange={v => {
                  const raw = SPREAD_Z_MIN + v * (SPREAD_Z_MAX - SPREAD_Z_MIN);
                  fqSet({ spreadZ: round(snapTo(raw, POS_SNAP_POINTS, POS_SNAP_TOL), 2) });
                }}
                valueText={(fq('spreadZ') || 0).toFixed(2)}
                onCommitText={raw => commitNum(raw, SPREAD_Z_MIN, SPREAD_Z_MAX, 2, val => fqSet({ spreadZ: val }))}
              />

              {/* Expand rotation — spins the WHOLE assembled arrangement (all
                  mirror/kaleido/repeat copies) rigidly around its own centre, so
                  you can orient the finished piece in 3D. Distinct from a single
                  wall's Rotation X/Y/Z above (which turns each mesh in place). */}
              <GradientSlider
                inline labelWidth={LABEL_W} label="Rotate X"
                value={((fq('spreadRotX') || 0) - ROT_MIN) / (ROT_MAX - ROT_MIN)}
                onChange={v => {
                  const raw = ROT_MIN + v * (ROT_MAX - ROT_MIN);
                  fqSet({ spreadRotX: round(snapTo(raw, ROT_SNAP_POINTS, ROT_SNAP_TOL), 1) });
                }}
                valueText={`${Math.round(fq('spreadRotX') || 0)}°`}
                onCommitText={raw => commitNum(raw, ROT_MIN, ROT_MAX, 0, val => fqSet({ spreadRotX: val }))}
              />
              <GradientSlider
                inline labelWidth={LABEL_W} label="Rotate Y"
                value={((fq('spreadRotY') || 0) - ROT_MIN) / (ROT_MAX - ROT_MIN)}
                onChange={v => {
                  const raw = ROT_MIN + v * (ROT_MAX - ROT_MIN);
                  fqSet({ spreadRotY: round(snapTo(raw, ROT_SNAP_POINTS, ROT_SNAP_TOL), 1) });
                }}
                valueText={`${Math.round(fq('spreadRotY') || 0)}°`}
                onCommitText={raw => commitNum(raw, ROT_MIN, ROT_MAX, 0, val => fqSet({ spreadRotY: val }))}
              />
              <GradientSlider
                inline labelWidth={LABEL_W} label="Rotate Z"
                value={((fq('spreadRotZ') || 0) - ROT_MIN) / (ROT_MAX - ROT_MIN)}
                onChange={v => {
                  const raw = ROT_MIN + v * (ROT_MAX - ROT_MIN);
                  fqSet({ spreadRotZ: round(snapTo(raw, ROT_SNAP_POINTS, ROT_SNAP_TOL), 1) });
                }}
                valueText={`${Math.round(fq('spreadRotZ') || 0)}°`}
                onCommitText={raw => commitNum(raw, ROT_MIN, ROT_MAX, 0, val => fqSet({ spreadRotZ: val }))}
              />

              {/* Live total — Frequency count × Mirror/Kaleido × Repeat module,
                  so you always know how many obstacles this module has. */}
              <div style={{ ...hintStyle, marginTop: 2, color: color.ink, fontWeight: 600 }}>
                {countDesignMeshes(g)} obstacle{countDesignMeshes(g) === 1 ? '' : 's'} in this module
              </div>

              {/* ══ ANIMATION — live motion (deform / rotate / scale), optionally
                  driven by the music or the notes. Shared across the whole art when
                  the shapes are ANCHORED; per-shape (edits the SELECTED shape) when
                  they're DISCONNECTED. Layered on the static pose; never baked. ══ */}
              <div style={sectionHeadStyle}>Animation</div>
              <FieldLabel>Preset</FieldLabel>
              <select
                value={(fq('anim') as DesignAnimConfig | undefined)?.preset ?? 'none'}
                onChange={e => {
                  const p = e.target.value as DesignAnimPreset;
                  // Keep the user's Time/Music/Notes choice across preset swaps.
                  fqSet({ anim: p === 'none' ? undefined : makeDesignAnim(p, (fq('anim') as DesignAnimConfig | undefined)?.sync) });
                }}
                style={selectStyle}
              >
                {DESIGN_ANIM_PRESETS.map(p => <option key={p} value={p}>{ANIM_LABEL[p]}</option>)}
              </select>
              {freqPart && <div style={{ ...hintStyle, marginTop: 0, color: 'var(--accent)' }}>Animating THIS mesh's own preset (meshes disconnected).</div>}
              {(() => {
                const a = fq('anim') as DesignAnimConfig | undefined;
                if (!a || a.preset === 'none') return null;
                const setAnim = (u: Partial<DesignAnimConfig>) => fqSet({ anim: { ...a, ...u } });
                return (
                  <>
                    <div style={hintStyle}>{ANIM_DESC[a.preset]}</div>

                    {/* Tentacle motion style: side-to-side sway, or a symmetric
                        expand/contract from the centre (opens & closes). */}
                    {a.preset === 'tentacle' && (
                      <>
                        <FieldLabel>Motion</FieldLabel>
                        <Segmented<'sway' | 'radial'>
                          options={[['sway', 'Sway'], ['radial', 'Open / Close']]}
                          value={a.tentacleMode ?? 'sway'}
                          onChange={v => setAnim({ tentacleMode: v })}
                        />
                      </>
                    )}

                    {/* The requested "reacts with the music or the notes" switch. */}
                    <FieldLabel>Reacts with</FieldLabel>
                    <Segmented<DesignAnimSync>
                      options={[['time', 'Time'], ['music', 'Music'], ['notes', 'Notes']]}
                      value={a.sync}
                      onChange={v => setAnim({ sync: v })}
                    />
                    <GradientSlider
                      inline labelWidth={LABEL_W} label="Speed"
                      value={(a.speed - ANIM_SPEED_MIN) / (ANIM_SPEED_MAX - ANIM_SPEED_MIN)}
                      onChange={v => setAnim({ speed: round(ANIM_SPEED_MIN + v * (ANIM_SPEED_MAX - ANIM_SPEED_MIN), 2) })}
                      valueText={a.speed.toFixed(2)}
                      onCommitText={raw => commitNum(raw, ANIM_SPEED_MIN, ANIM_SPEED_MAX, 2, val => setAnim({ speed: val }))}
                    />
                    <GradientSlider
                      inline labelWidth={LABEL_W} label="Strength"
                      value={(a.amount - ANIM_AMT_MIN) / (ANIM_AMT_MAX - ANIM_AMT_MIN)}
                      onChange={v => setAnim({ amount: round(ANIM_AMT_MIN + v * (ANIM_AMT_MAX - ANIM_AMT_MIN), 2) })}
                      valueText={a.amount.toFixed(2)}
                      onCommitText={raw => commitNum(raw, ANIM_AMT_MIN, ANIM_AMT_MAX, 2, val => setAnim({ amount: val }))}
                    />
                    {designAnimUsesSpread(a.preset) && (
                      <GradientSlider
                        inline labelWidth={LABEL_W} label="Spread"
                        value={(a.phase - ANIM_PHASE_MIN) / (ANIM_PHASE_MAX - ANIM_PHASE_MIN)}
                        onChange={v => setAnim({ phase: round(ANIM_PHASE_MIN + v * (ANIM_PHASE_MAX - ANIM_PHASE_MIN), 2) })}
                        valueText={a.phase.toFixed(2)}
                        onCommitText={raw => commitNum(raw, ANIM_PHASE_MIN, ANIM_PHASE_MAX, 2, val => setAnim({ phase: val }))}
                      />
                    )}
                    {a.sync === 'music' && <div style={hintStyle}>Reacts to the song's energy while it plays; previews at ~1 beat/sec while paused.</div>}
                    {a.sync === 'notes' && <div style={hintStyle}>Pulses on each note while playing; previews at ~1 beat/sec while paused.</div>}

                    {/* Beat-reaction envelope — shape each note hit with an in
                        (accelerating) and out (decelerating). Notes sync only. */}
                    {a.sync === 'notes' && (
                      <>
                        <FieldLabel>Beat reaction</FieldLabel>
                        <GradientSlider
                          inline labelWidth={LABEL_W} label="In (attack)"
                          value={((a.attack ?? 0.04) - ANIM_ATK_MIN) / (ANIM_ATK_MAX - ANIM_ATK_MIN)}
                          onChange={v => setAnim({ attack: round(ANIM_ATK_MIN + v * (ANIM_ATK_MAX - ANIM_ATK_MIN), 3) })}
                          valueText={`${(a.attack ?? 0.04).toFixed(2)}b`}
                          onCommitText={raw => commitNum(raw, ANIM_ATK_MIN, ANIM_ATK_MAX, 3, val => setAnim({ attack: val }))}
                        />
                        <GradientSlider
                          inline labelWidth={LABEL_W} label="Out (release)"
                          value={((a.release ?? 0.45) - ANIM_REL_MIN) / (ANIM_REL_MAX - ANIM_REL_MIN)}
                          onChange={v => setAnim({ release: round(ANIM_REL_MIN + v * (ANIM_REL_MAX - ANIM_REL_MIN), 2) })}
                          valueText={`${(a.release ?? 0.45).toFixed(2)}b`}
                          onCommitText={raw => commitNum(raw, ANIM_REL_MIN, ANIM_REL_MAX, 2, val => setAnim({ release: val }))}
                        />
                        <GradientSlider
                          inline labelWidth={LABEL_W} label="Accel (in)"
                          value={((a.easeIn ?? 1.5) - ANIM_EASE_MIN) / (ANIM_EASE_MAX - ANIM_EASE_MIN)}
                          onChange={v => setAnim({ easeIn: round(ANIM_EASE_MIN + v * (ANIM_EASE_MAX - ANIM_EASE_MIN), 2) })}
                          valueText={`${(a.easeIn ?? 1.5).toFixed(1)}×`}
                          onCommitText={raw => commitNum(raw, ANIM_EASE_MIN, ANIM_EASE_MAX, 2, val => setAnim({ easeIn: val }))}
                        />
                        <GradientSlider
                          inline labelWidth={LABEL_W} label="Decel (out)"
                          value={((a.easeOut ?? 2.2) - ANIM_EASE_MIN) / (ANIM_EASE_MAX - ANIM_EASE_MIN)}
                          onChange={v => setAnim({ easeOut: round(ANIM_EASE_MIN + v * (ANIM_EASE_MAX - ANIM_EASE_MIN), 2) })}
                          valueText={`${(a.easeOut ?? 2.2).toFixed(1)}×`}
                          onCommitText={raw => commitNum(raw, ANIM_EASE_MIN, ANIM_EASE_MAX, 2, val => setAnim({ easeOut: val }))}
                        />
                      </>
                    )}

                    {/* Distribution — stagger the reaction across the walls so it
                        reacts by segments instead of moving as one bar. Notes/Music. */}
                    {(a.sync === 'notes' || a.sync === 'music') && (
                      <>
                        <FieldLabel>Distribution</FieldLabel>
                        <select
                          value={a.dist ?? 'uniform'}
                          onChange={e => setAnim({ dist: e.target.value as DesignAnimDist })}
                          style={selectStyle}
                          title="How the reaction spreads across the walls"
                        >
                          {DESIGN_ANIM_DISTS.map(d => <option key={d} value={d}>{ANIM_DIST_LABEL[d]}</option>)}
                        </select>
                        {(a.dist ?? 'uniform') !== 'uniform' && (
                          <>
                            <GradientSlider
                              inline labelWidth={LABEL_W} label="Stagger"
                              value={((a.stagger ?? 0.5) - ANIM_STAG_MIN) / (ANIM_STAG_MAX - ANIM_STAG_MIN)}
                              onChange={v => setAnim({ stagger: round(ANIM_STAG_MIN + v * (ANIM_STAG_MAX - ANIM_STAG_MIN), 2) })}
                              valueText={`${(a.stagger ?? 0.5).toFixed(2)}b`}
                              onCommitText={raw => commitNum(raw, ANIM_STAG_MIN, ANIM_STAG_MAX, 2, val => setAnim({ stagger: val }))}
                            />
                            <div style={hintStyle}>Each beat lights a fresh band of segments (it rotates to areas that reacted least), so it evolves organically instead of repeating the same scatter.</div>
                          </>
                        )}
                      </>
                    )}
                  </>
                );
              })()}

              {/* ══ GLOBAL POSITION — move the whole decor in X / Y / Z ══ */}
              <div style={sectionHeadStyle}>Global Position</div>
              <GradientSlider
                inline labelWidth={LABEL_W} label="Move X"
                value={(g.originX - MOVE_XY_MIN) / (MOVE_XY_MAX - MOVE_XY_MIN)}
                onChange={v => {
                  const raw = MOVE_XY_MIN + v * (MOVE_XY_MAX - MOVE_XY_MIN);
                  patch({ originX: round(snapTo(raw, POS_SNAP_POINTS, POS_SNAP_TOL), 2) });
                }}
                valueText={g.originX.toFixed(2)}
                onCommitText={raw => commitNum(raw, MOVE_XY_MIN, MOVE_XY_MAX, 2, val => patch({ originX: val }))}
              />

              <GradientSlider
                inline labelWidth={LABEL_W} label="Move Y"
                value={(g.originY - MOVE_XY_MIN) / (MOVE_XY_MAX - MOVE_XY_MIN)}
                onChange={v => {
                  const raw = MOVE_XY_MIN + v * (MOVE_XY_MAX - MOVE_XY_MIN);
                  patch({ originY: round(snapTo(raw, POS_SNAP_POINTS, POS_SNAP_TOL), 2) });
                }}
                valueText={g.originY.toFixed(2)}
                onCommitText={raw => commitNum(raw, MOVE_XY_MIN, MOVE_XY_MAX, 2, val => patch({ originY: val }))}
              />

              <GradientSlider
                inline labelWidth={LABEL_W} label="Move Z"
                value={(g.beat - MOVE_Z_MIN) / (MOVE_Z_MAX - MOVE_Z_MIN)}
                onChange={v => {
                  const raw = MOVE_Z_MIN + v * (MOVE_Z_MAX - MOVE_Z_MIN);
                  patch({ beat: Math.max(0, round(snapTo(raw, POS_SNAP_POINTS, POS_SNAP_TOL), 2)) });
                }}
                valueText={g.beat.toFixed(2)}
                onCommitText={raw => commitNum(raw, MOVE_Z_MIN, MOVE_Z_MAX, 2, val => patch({ beat: Math.max(0, val) }))}
              />

              {/* Global rotation — orient the whole placed wall art in 3D
                  (rotates rigidly around its placed centre). Move places it,
                  Rotate aims it. */}
              <GradientSlider
                inline labelWidth={LABEL_W} label="Rotate X"
                value={((g.originRotX || 0) - ROT_MIN) / (ROT_MAX - ROT_MIN)}
                onChange={v => {
                  const raw = ROT_MIN + v * (ROT_MAX - ROT_MIN);
                  patch({ originRotX: round(snapTo(raw, ROT_SNAP_POINTS, ROT_SNAP_TOL), 1) });
                }}
                valueText={`${Math.round(g.originRotX || 0)}°`}
                onCommitText={raw => commitNum(raw, ROT_MIN, ROT_MAX, 0, val => patch({ originRotX: val }))}
              />
              <GradientSlider
                inline labelWidth={LABEL_W} label="Rotate Y"
                value={((g.originRotY || 0) - ROT_MIN) / (ROT_MAX - ROT_MIN)}
                onChange={v => {
                  const raw = ROT_MIN + v * (ROT_MAX - ROT_MIN);
                  patch({ originRotY: round(snapTo(raw, ROT_SNAP_POINTS, ROT_SNAP_TOL), 1) });
                }}
                valueText={`${Math.round(g.originRotY || 0)}°`}
                onCommitText={raw => commitNum(raw, ROT_MIN, ROT_MAX, 0, val => patch({ originRotY: val }))}
              />
              <GradientSlider
                inline labelWidth={LABEL_W} label="Rotate Z"
                value={((g.originRotZ || 0) - ROT_MIN) / (ROT_MAX - ROT_MIN)}
                onChange={v => {
                  const raw = ROT_MIN + v * (ROT_MAX - ROT_MIN);
                  patch({ originRotZ: round(snapTo(raw, ROT_SNAP_POINTS, ROT_SNAP_TOL), 1) });
                }}
                valueText={`${Math.round(g.originRotZ || 0)}°`}
                onCommitText={raw => commitNum(raw, ROT_MIN, ROT_MAX, 0, val => patch({ originRotZ: val }))}
              />

              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button style={dupBtnStyle} onClick={() => duplicateDesignShape(g.id, `${wallLabel(g)} (Copy)`)}>Duplicate</button>
                <button style={deleteBtnStyle} onClick={() => removeDesignShapes(selectedIds)}>
                  <IcTrash size={13} /> {multi ? `Delete ${selectedIds.length}` : 'Delete'}
                </button>
              </div>

              {/* ══ RULES — platform limits worth knowing while designing ══ */}
              <div style={sectionHeadStyle}>Rules</div>
              <div style={hintStyle}>
                The maximum number of solid meshes is <b style={{ color: color.ink }}>500</b> per
                shape. Past that limit they automatically render as{' '}
                <b style={{ color: color.ink }}>wireframe</b>.
              </div>
              {countDesignMeshes(g) > QUEST_SOLID_WALL_CAP && (
                <div style={{ ...hintStyle, marginTop: 4, color: '#F59E0B', fontWeight: 600 }}>
                  This shape has {countDesignMeshes(g)} meshes — over 500, so solid meshes will
                  render as wireframe on Quest (the editor still shows your chosen style).
                </div>
              )}
            </div>
        </>
      )}
    </div>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <div style={{ color: color.inkFaint, fontSize: 11, fontWeight: 600, marginTop: 2 }}>{children}</div>;
}


function Segmented<T extends string>({ options, value, onChange }: {
  options: [T, string][];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {options.map(([val, label]) => {
        const active = val === value;
        return (
          <button
            key={val}
            onClick={() => onChange(val)}
            style={{
              flex: 1, padding: '7px 6px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap',
              fontFamily: font.sans, fontSize: 11, fontWeight: 600,
              background: active
                ? 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.25), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.15))'
                : 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
              border: active
                ? '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)'
                : '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h)/0.15)',
              color: active ? 'var(--accent)' : color.accentSoft,
            } as React.CSSProperties}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/** Same visual as Segmented, but each button TOGGLES its own membership in
 *  `value` (an array) instead of replacing the whole selection — lets you check
 *  more than one option at once (e.g. repeat along X AND Z together). Always
 *  keeps at least one selected — clicking the last remaining one is a no-op. */
function MultiSegmented<T extends string>({ options, value, onChange }: {
  options: [T, string][];
  value: T[];
  onChange: (v: T[]) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {options.map(([val, label]) => {
        const active = value.includes(val);
        return (
          <button
            key={val}
            onClick={() => {
              if (active) { if (value.length > 1) onChange(value.filter(v => v !== val)); }
              else onChange([...value, val]);
            }}
            style={{
              flex: 1, padding: '7px 6px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap',
              fontFamily: font.sans, fontSize: 11, fontWeight: 600,
              background: active
                ? 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.25), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.15))'
                : 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
              border: active
                ? '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)'
                : '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h)/0.15)',
              color: active ? 'var(--accent)' : color.accentSoft,
            } as React.CSSProperties}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ── Styles (mirror FlashEventPopup) ────────────────────────────────────────────

const panelStyle: React.CSSProperties = {
  // Docked just to the LEFT of the right sidebar (not overlapping it). Top aligns
  // with the sidebar's first card (shell pad + the fullscreen/settings header row).
  position: 'fixed', top: 'calc(var(--shell-pad-y, 16px) + 46px)', right: 'calc(var(--sidebar-x, 16px) + var(--sr-right-w, 245px) + 12px)', zIndex: 60,
  width: 'min(300px, calc(100vw - 24px))', maxHeight: 'calc(100vh - 110px)', overflowY: 'auto',
  // No top padding: the pinned Meshes bar (position:sticky top:0) then hugs the very
  // top with no gap for scrolling content to peek through. The header restores the
  // 14px top breathing room itself (paddingTop below).
  padding: '0 14px 14px', borderRadius: 16,
  background: 'oklch(0.09 calc(0.018 * var(--tint-c)) var(--tint-h) / 0.92)',
  border: '1.5px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.4)',
  boxShadow: '0 12px 40px rgba(0,0,0,0.55)',
  backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
  display: 'flex', flexDirection: 'column', gap: 10, fontFamily: font.sans,
};
const headerStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2, paddingTop: 14 };
const titleStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 7, color: color.ink, fontSize: 13, fontWeight: 700, letterSpacing: 0.2 };
// Circular header button — shared by the Back arrow (left) and Close (right)
// so they read as a matched pair; mirrors the editor's top-of-sidebar Back
// arrow (EditorBackHeader), scaled down to sit beside the panel title.
const circleHeaderBtnStyle: React.CSSProperties = {
  flexShrink: 0, width: 30, height: 30, borderRadius: '50%', padding: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  color: 'var(--accent)', cursor: 'pointer',
};
const hintStyle: React.CSSProperties = { color: color.inkFaint, fontSize: 11, lineHeight: 1.45 };
const emptyStyle: React.CSSProperties = { color: color.inkFaint, fontSize: 11, padding: '8px 4px', textAlign: 'center' };
const listStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 150, overflowY: 'auto' };
const rowStyle = (active: boolean, dim = false): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
  padding: '7px 12px', borderRadius: 14, cursor: 'pointer',
  fontFamily: font.sans, fontSize: 11, fontWeight: 600,
  background: active
    ? 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.22), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.12))'
    : 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.35)',
  border: active
    ? '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)'
    : '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h)/0.12)',
  color: active ? 'var(--accent)' : color.ink,
  opacity: dim ? 0.5 : 1,
});
const addBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  padding: '9px 10px', borderRadius: 999, cursor: 'pointer',
  background: 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.25), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.15))',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)',
  color: 'var(--accent)', fontSize: 12, fontWeight: 600, fontFamily: font.sans,
};
const editorStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 9, marginTop: 4,
  paddingTop: 10, borderTop: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h)/0.12)',
};
const sectionHeadStyle: React.CSSProperties = {
  color: 'var(--accent)', fontSize: 9, fontWeight: 700, letterSpacing: 1.2,
  textTransform: 'uppercase', marginTop: 6,
  borderTop: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h)/0.1)', paddingTop: 8,
};
// Applied to the Meshes wrapper when pinned: sticks it to the panel's scroll top
// with a near-opaque backdrop (hides the properties scrolling underneath) and a
// soft drop shadow to lift it. Matches the panel tint so it's invisible until stuck.
const stickyMeshesStyle: React.CSSProperties = {
  position: 'sticky', top: 0, zIndex: 5,
  background: 'oklch(0.10 calc(0.02 * var(--tint-c)) var(--tint-h) / 0.985)',
  paddingBottom: 8, marginBottom: 1,
  boxShadow: '0 10px 14px -10px rgba(0,0,0,0.65)',
};
// Small round pin toggle at the right of the "Meshes" header.
const pinBtnStyle = (active: boolean): React.CSSProperties => ({
  flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 22, height: 22, borderRadius: 999, padding: 0, cursor: 'pointer',
  background: active ? 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.18)' : 'transparent',
  border: active
    ? '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)'
    : '1px solid transparent',
  color: active ? 'var(--accent)' : color.inkFaint,
});
// Compact "Add mesh" pill in the Meshes header — same 22px height as the pin ring.
const addMeshMiniStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 4,
  height: 22, padding: '0 10px', borderRadius: 999, cursor: 'pointer',
  background: 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.25), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.15))',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)',
  color: 'var(--accent)', fontSize: 10, fontWeight: 700, fontFamily: font.sans,
  textTransform: 'none', letterSpacing: 0.2, whiteSpace: 'nowrap',
};
// Plain field look — used by the text <input> (Name). Selects extend this below.
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 14px', borderRadius: 999,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h)/0.2)',
  color: color.ink, fontSize: 11, fontFamily: font.sans, cursor: 'pointer',
};
// The native <select> arrow sits flush to the right edge and ignores padding, so
// hide it and draw our own chevron inset 14px from the edge (matches the left pad).
const SELECT_CHEVRON =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23cdc7e6' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")";
const selectStyle: React.CSSProperties = {
  ...inputStyle,
  appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
  padding: '8px 34px 8px 14px',
  backgroundImage: SELECT_CHEVRON,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 14px center',
};
const colorRowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10 };
const colorInputStyle: React.CSSProperties = {
  width: 34, height: 26, padding: 0, border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h)/0.25)',
  borderRadius: 6, background: 'none', cursor: 'pointer',
};
const partEditorStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 8,
  padding: '10px 10px 4px', marginTop: 2,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.25)',
  borderRadius: 10,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h)/0.15)',
};
const rowTrashStyle: React.CSSProperties = {
  flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 26, height: 26, borderRadius: 6, cursor: 'pointer',
  background: 'transparent', border: 'none', color: '#F87171',
};
// Small "Public" pill shown on published library rows.
const publicBadgeStyle: React.CSSProperties = {
  flexShrink: 0, padding: '2px 8px', borderRadius: 999,
  fontFamily: font.sans, fontSize: 9, fontWeight: 700, letterSpacing: 0.3,
  background: 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.18)',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)',
  color: 'var(--accent)',
};
const rowDuplicateStyle: React.CSSProperties = {
  flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 26, height: 26, borderRadius: 6, cursor: 'pointer',
  background: 'transparent', border: 'none', color: color.inkFaint,
};
const combineBtnStyle = (active: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', gap: 5,
  padding: '4px 9px', borderRadius: 999, cursor: 'pointer',
  fontFamily: font.sans, fontSize: 9.5, fontWeight: 700, letterSpacing: 0.3, textTransform: 'none',
  background: active
    ? 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.25), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.15))'
    : 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  border: active
    ? '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)'
    : '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h)/0.15)',
  color: active ? 'var(--accent)' : color.accentSoft,
});
const dupBtnStyle: React.CSSProperties = {
  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  padding: '9px 10px', borderRadius: 999, cursor: 'pointer',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h)/0.2)',
  color: color.accentSoft, fontSize: 12, fontWeight: 600, fontFamily: font.sans,
};
const deleteBtnStyle: React.CSSProperties = {
  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  padding: '9px 10px', borderRadius: 999, cursor: 'pointer',
  background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.35)',
  color: '#F87171', fontSize: 12, fontWeight: 600, fontFamily: font.sans,
};
